import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { categoryApi } from "@/lib/api";
import { toast } from "sonner";

export default function CategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("sparkles");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const load = () => categoryApi.list().then(setItems);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      const cat = await categoryApi.create({ name: name.trim(), icon });
      setItems((p) => [...p, cat]);
      setName(""); toast.success("Category added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await categoryApi.remove(id);
    setItems((p) => p.filter((c) => c.id !== id));
    toast.success("Deleted");
  };

  const toggleEnabled = async (cat, enabled) => {
    const upd = await categoryApi.update(cat.id, { name: cat.name, enabled });
    setItems((p) => p.map((c) => (c.id === cat.id ? upd : c)));
  };

  const saveEdit = async (cat) => {
    if (!editName.trim()) return;
    const upd = await categoryApi.update(cat.id, { name: editName.trim(), enabled: cat.enabled });
    setItems((p) => p.map((c) => (c.id === cat.id ? upd : c)));
    setEditing(null);
    toast.success("Updated");
  };

  return (
    <div>
      <h1 className="font-display text-4xl font-black mb-1">Categories</h1>
      <p className="text-gray-500 mb-6">Curate the categories users see on the home page.</p>

      <div className="bg-white rounded-2xl border border-black/10 p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="new-category-name"
            placeholder="New category name (e.g. Wellness)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-11 rounded-full"
          />
          <Input
            data-testid="new-category-icon"
            placeholder="Icon (music, mic, flame…)"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            className="h-11 rounded-full sm:max-w-xs"
          />
          <Button
            data-testid="new-category-add"
            onClick={create}
            className="h-11 rounded-full bg-[#FA003F] hover:bg-[#C40031] font-bold px-6"
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-black/10 overflow-hidden divide-y divide-black/5">
        {items.map((c) => (
          <div key={c.id} className="px-6 py-4 flex items-center gap-3">
            {editing === c.id ? (
              <>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="max-w-sm rounded-full"
                  data-testid={`edit-category-input-${c.key}`}
                />
                <Button onClick={() => saveEdit(c)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-9">
                  <Check className="h-4 w-4" />
                </Button>
                <Button onClick={() => setEditing(null)} variant="outline" className="rounded-full h-9">
                  <X className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <div className="font-bold">{c.name}</div>
                  <div className="text-xs text-gray-500">key: {c.key} · icon: {c.icon || "—"}</div>
                </div>
                <Switch
                  data-testid={`category-toggle-${c.key}`}
                  checked={c.enabled}
                  onCheckedChange={(v) => toggleEnabled(c, v)}
                  className="data-[state=checked]:bg-[#FA003F]"
                />
                <Button
                  variant="outline"
                  className="rounded-full h-9"
                  data-testid={`category-edit-${c.key}`}
                  onClick={() => { setEditing(c.id); setEditName(c.name); }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full h-9 text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`category-delete-${c.key}`}
                  onClick={() => remove(c.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="px-6 py-10 text-gray-500 text-center">No categories yet.</div>
        )}
      </div>
    </div>
  );
}
